import { get } from 'lodash';
import mimeDB from 'mime-db';
import i18n from 'i18n';
import { ScreenProperty } from 'core/channelGroup/ChannelGroup';
import { renderErrors } from 'containers/Creatives/CreativeSetupFlow/FlowSteps/SubSteps/FormContent/CreativeFormHintRenderFunction';
import { formatBytes } from 'utils/StringUtil';

/**
 * 電子紙預設圖片（裝置沒有廣告可投時的填充圖）的驗證規則。ECOPD-5581。
 *
 * 規則沿用既有 outdoor 圖片素材那一套，但**刻意沒有沿用它的 screenDisplayType
 * === SINGLE 條件**：那個條件會讓連台／同播渠道整段跳過大小檢查，套到這裡等於
 * 「有些電子紙渠道的圖片不驗大小」，而且沒有任何徵兆。這裡一律驗。
 */

/** 格式白名單。與既有 outdoor 圖片一致，且都落在元太接受的格式內。不含 gif、不含 bmp。 */
export const EPAPER_DEFAULT_IMAGE_VALID_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png'
];

/** 2 MB。沿用既有 outdoor 圖片素材的上限。 */
export const EPAPER_DEFAULT_IMAGE_MAX_BYTES = 2097152;

const hasFile = (
  fileData: ImageFieldData
): fileData is { file: File; width: number; height: number } =>
  !!fileData && !!fileData.file;

export function validateEpaperDefaultImage(
  fileData: ImageFieldData,
  screenProperty?: ScreenProperty
): string | JSX.Element | undefined {
  // 必填：畫面上必須真的有一張圖。
  // 前端能看到的只有圖片 URL —— 元太圖庫的 image id 不在 wire 上。遷移進來的舊渠道
  // 只有那個 id、沒有 URL，前端無從預覽也無從確認掛的是哪張，所以一律要求重新上傳
  // 一次。後端那側仍然只檢 image id，兩邊規則不同是刻意的。
  if (!fileData || (!fileData.file && !fileData.url)) {
    return i18n.t('formValidate.labels.emptyError');
  }

  // 沒有換圖（只有既有的 url）就不必重驗格式與尺寸 —— 那張圖上傳當下已經驗過，
  // 而且前端手上沒有 File 可以量。
  if (!hasFile(fileData)) {
    return undefined;
  }

  const file = fileData.file;
  if (!EPAPER_DEFAULT_IMAGE_VALID_TYPES.includes(file.type)) {
    const extensions = get(mimeDB[file.type], 'extensions', ['Unknown']);
    return i18n.t('creativeSetupFlow.labels.typeErrorHint', {
      type: extensions[0]
    });
  }

  const errors: (string | JSX.Element)[] = [];

  const targetWidth = screenProperty?.screenPixelWidth;
  const targetHeight = screenProperty?.screenPixelHeight;
  if (!targetWidth || !targetHeight) {
    // 解析度是 RMax 同步來的，電子紙渠道照契約一定要有。真的沒有時要講出來，
    // 不能默默放行 —— 那會讓使用者上傳一張尺寸錯的圖卻以為過了。
    errors.push(
      i18n.t('channelGroupForm.labels.epaperDefaultImageNoScreenSizeError')
    );
  } else if (
    // 尺寸要剛剛好等於渠道的解析度，不給容差：電子紙的填充圖是整版鋪滿，差一個
    // 像素就會有邊或被裁掉。
    fileData.width !== targetWidth ||
    fileData.height !== targetHeight
  ) {
    errors.push(
      i18n.t('creativeSetupFlow.labels.sizeErrorHint2', {
        size1: `${fileData.width} x ${fileData.height}`,
        size2: `${targetWidth} x ${targetHeight}`
      })
    );
  }

  if (file.size > EPAPER_DEFAULT_IMAGE_MAX_BYTES) {
    errors.push(
      i18n.t('creativeSetupFlow.labels.storageErrorHint', {
        storage1: formatBytes(file.size),
        storage2: '2MB'
      })
    );
  }

  return errors.length > 0 ? renderErrors(errors) : undefined;
}
